import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleLinkedInCallback } from '../services/linkedinAuth';
import SessionManager from '../utils/sessionManager';
import Icon from '../components/atoms/Icon';

const LinkedInCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing LinkedIn sign-in...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for errors from LinkedIn
        if (error) {
          setStatus('error');
          setMessage(errorDescription || 'LinkedIn authentication was cancelled or failed.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate code
        if (!code) {
          setStatus('error');
          setMessage('No authorization code received from LinkedIn.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Extract role from state
        const role = state?.split('_')[0] || 'candidate';

        setMessage('Verifying with LinkedIn...');

        // Handle callback
        const response = await handleLinkedInCallback(code, state, role);

        setStatus('success');
        setMessage('Success! Redirecting...');

        // Store session
        SessionManager.setSession(role, response);

        // Redirect to home
        setTimeout(() => {
          navigate('/');
        }, 1500);

      } catch (error) {
        console.error('LinkedIn callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to complete LinkedIn sign-in. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 animate-pulse">
                <Icon name="sync" size={32} className="text-blue-600 animate-spin" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Completing Sign-In
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Icon name="check_circle" size={32} className="text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Success!
            </h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <Icon name="error" size={32} className="text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LinkedInCallbackPage;
